import { ApolloClient } from '@apollo/client'
import { state } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import ramdaPipe from 'ramda/src/pipe'
import {
  BehaviorSubject,
  combineLatest,
  delay,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  interval,
  map,
  Observable,
  of,
  scan,
  startWith,
  Subject,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs'
import { catchError, skipWhile, switchMap, take } from 'rxjs/operators'

import {
  CartFragment,
  PlaceOrderDocument,
  PlaceOrderMutation,
  PlaceOrderMutationVariables,
  PlaceOrderWithPaymentMethodDocument,
  PlaceOrderWithPaymentMethodMutation,
  PlaceOrderWithPaymentMethodMutationVariables,
} from '../../../graphql/magento'
import { isNotNull } from '../../../utils/collectionTools'
import { CustomError, InvalidResponseError } from '../../../utils/errorTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { mapRight, switchMapRight } from '../../../utils/rx/operators'
import { latestAccessToken$ } from '../../auth/state'
import { CompletedCart } from '../../completed-carts/types'
import { latestApolloClient$ } from '../../graphql/apollo'
import { UnexpectedCartError } from '../errors'
import { latestCustomerCart$ } from './cart'

export type PlaceOrderTaskResult =
  | {
      _tag: 'Left'
      error: Error
    }
  | {
      _tag: 'Right'
      data: CompletedCart
    }

export class PaymentDetailsMissingError extends CustomError {
  override name = 'PaymentDetailsMissingError'
}

export class PlaceOrderTaskMissingError extends CustomError {
  override name = 'PlaceOrderTaskMissingError'
}

export interface PlaceOrderTask {
  // supply `cart` to validate task before running (compare with cartId bound to task) and
  // produce `CompletedCart` on success
  effect: (cart: CartFragment, dispensarySlug: string) => Observable<PlaceOrderTaskResult>
  sensitive: boolean
}

const cleanupTaskOnSuccessTick$ = new Subject<void>()

// tick after assigning sensitive task (e.g. bound to CC data) to start reset clock
const sensitiveTaskTick$ = new Subject<void>()

// timeout after 15 minutes
const SENSITIVE_TASK_TIMEOUT_SECONDS = 900

const [placeOrderTaskInput$, setPlaceOrderTaskInput] = createSignal<PlaceOrderTask | undefined>()

export { setPlaceOrderTaskInput }

// hold place order task prepared from payment details
// - task reads `cartId` from parameters to validate task before running
export const latestPlaceOrderTask$ = state<PlaceOrderTask | undefined>(
  placeOrderTaskInput$.pipe(
    tap(
      (task) =>
        // trigger timeout to clear sensitive data after window of time
        task?.sensitive && sensitiveTaskTick$.next(),
    ),
  ),
  undefined,
)

export const placeOrderTaskState$ = state<'not present' | 'present'>(
  latestPlaceOrderTask$.pipe(
    map((placeOrderTask) => (placeOrderTask ? 'present' : 'not present')),
    distinctUntilChanged(),
  ),
)

const [placeOrderTaskParamsInput$, applyPlaceOrderTaskParams] = createSignal<{
  cart: CartFragment
  dispensarySlug: string
}>()

export { applyPlaceOrderTaskParams }

export const placeOrderTaskApplicationLoading$ = new BehaviorSubject(false)

export const placeOrderTaskApplication$: Observable<PlaceOrderTaskResult> =
  placeOrderTaskParamsInput$.pipe(
    switchMap(({ cart, dispensarySlug }) =>
      latestPlaceOrderTask$.pipe(
        take(1),
        tap(() => placeOrderTaskApplicationLoading$.next(true)),
        switchMap((placeOrderTask) =>
          !placeOrderTask
            ? // safety check: this should never happen
              of({ _tag: 'Left' as const, error: new PlaceOrderTaskMissingError() })
            : placeOrderTask.effect(cart, dispensarySlug).pipe(
                tap((result) => {
                  if (result._tag === 'Right') {
                    cleanupTaskOnSuccessTick$.next()
                    // order placed, wait for cart to update before setting loading to false
                    return latestCustomerCart$.pipe(
                      skipWhile(
                        (customerCart) =>
                          customerCart._tag === 'Right' && customerCart.data.customerCart === cart,
                      ),
                      take(1),
                      map(() => {
                        placeOrderTaskApplicationLoading$.next(false)
                      }),
                    )
                  }
                  if (placeOrderTask.sensitive) {
                    // resume sensitive data reset clock after task failed
                    sensitiveTaskTick$.next()
                  }
                  // task failed, set loading to false
                  placeOrderTaskApplicationLoading$.next(false)
                  return EMPTY
                }),
              ),
        ),
      ),
    ),
  )

export const cleanupTaskOnSuccess$ = cleanupTaskOnSuccessTick$.pipe(
  switchMap(() =>
    latestPlaceOrderTask$.pipe(
      take(1),
      filter(isNotNull),
      delay(30000),
      tap(() => setPlaceOrderTaskInput(undefined)),
    ),
  ),
)

export const sensitiveTaskResetClock$ = state<number>(
  sensitiveTaskTick$.pipe(
    switchMap(() =>
      latestPlaceOrderTask$.pipe(
        take(1),
        filter(isNotNull),
        switchMap(() => interval(1000)),
        map(() => -1),
        scan((acc: number, curr: number) => acc + curr, SENSITIVE_TASK_TIMEOUT_SECONDS),
        takeWhile((val) => val >= 0),
        startWith(SENSITIVE_TASK_TIMEOUT_SECONDS),
        // pause clock when place order task applied
        takeUntil(placeOrderTaskParamsInput$),
      ),
    ),
  ),
)

// clear task when reset clock reaches 0
export const sensitiveTaskResetOnTimeout$ = sensitiveTaskResetClock$.pipe(
  filter((val) => val === 0),
  tap(() => setPlaceOrderTaskInput(undefined)),
)

/**
 * Try to create a place order task from existing cart. If more information needed, return
 * undefined (so information can be collected on payment details page).
 */
export const placeOrderTaskFromCart = ({
  id: cartId,
  selected_payment_method: selectedPaymentMethod,
}: CartFragment): PlaceOrderTask | undefined =>
  selectedPaymentMethod?.code === 'free' || selectedPaymentMethod?.code === 'purchaseorder'
    ? createVanillaPlaceOrderTask({ cartId, dispensarySlug: '' })
    : undefined

/**
 * Place order without payment method mutation (for purchase order)
 */
const placeOrderFactory = (
  client: ApolloClient<unknown>,
  token: string,
  variables: PlaceOrderMutationVariables,
) =>
  from(
    client.mutate({
      mutation: PlaceOrderDocument,
      variables,
      context: { token },
      errorPolicy: 'none',
    }),
  ).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    tap(() => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'customerCart' })
      client.cache.gc()
    }),
    catchError(coerceCaughtToLeft),
  )

/**
 * Place order with payment method mutation (for credit card). Supports sending CVV when placing order.
 */
const placeOrderWithPaymentMethodFactory = (
  client: ApolloClient<unknown>,
  token: string,
  variables: PlaceOrderWithPaymentMethodMutationVariables,
) =>
  from(
    client.mutate({
      mutation: PlaceOrderWithPaymentMethodDocument,
      variables,
      context: { token },
      errorPolicy: 'none',
    }),
  ).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    tap(() => {
      // invalidate saved credit card query in case new card was saved
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'tokenBaseCards' })
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'customerCart' })
      client.cache.gc()
    }),
    catchError(coerceCaughtToLeft),
  )

type ValidPlaceOrderMutation = PlaceOrderMutation & {
  placeOrder: NonNullable<PlaceOrderWithPaymentMethodMutation['placeOrder']>
}

type ValidPlaceOrderWithPaymentMethodMutation = PlaceOrderWithPaymentMethodMutation & {
  placeOrder: NonNullable<PlaceOrderWithPaymentMethodMutation['placeOrder']>
  setPaymentMethodOnCart: NonNullable<PlaceOrderWithPaymentMethodMutation['setPaymentMethodOnCart']>
}

function isValidPlaceOrderMutation(
  data: PlaceOrderMutation | null | undefined,
): data is ValidPlaceOrderMutation {
  return Boolean(data?.placeOrder?.order)
}

function isValidPlaceOrderWithPaymentMethodMutation(
  data: PlaceOrderWithPaymentMethodMutation | null | undefined,
): data is ValidPlaceOrderWithPaymentMethodMutation {
  return Boolean(data?.placeOrder?.order) && Boolean(data?.setPaymentMethodOnCart?.cart)
}

/**
 * Create task to request placeOrder mutation by itself (for free or purchase order payment methods)
 */
export const createVanillaPlaceOrderTask: (params: PlaceOrderMutationVariables) => PlaceOrderTask =
  ramdaPipe(
    (params) => (cartToValidate: CartFragment) =>
      of(
        params.cartId === cartToValidate.id
          ? { _tag: 'Right' as const, data: params }
          : {
              _tag: 'Left' as const,
              error: new UnexpectedCartError(
                `Cart ID differs from state (expected: ${params.cartId}, actual: ${cartToValidate})`,
              ),
            },
      ),
    (validateParams) => (cart, dispensarySlug) =>
      validateParams(cart).pipe(
        switchMapRight(({ data: params }) =>
          combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
            take(1),
            switchMap(([client, token]) =>
              placeOrderFactory(client, token, { ...params, dispensarySlug }).pipe(
                mapRight(({ data }) =>
                  !isValidPlaceOrderMutation(data)
                    ? {
                        _tag: 'Left' as const,
                        error: new InvalidResponseError(
                          'Incomplete data returned from placeOrder mutation',
                        ),
                      }
                    : { _tag: 'Right' as const, data },
                ),
              ),
            ),
            // build CompletedCart from input cart and response data
            mapRight(({ data }) => ({
              _tag: 'Right' as const,
              data: {
                cart,
                order: data.placeOrder.order,
                completedAt: new Date().toISOString(),
              },
            })),
          ),
        ),
      ),
    (effect: PlaceOrderTask['effect']) => ({
      effect,
      sensitive: false,
    }),
  )

/**
 * Create task to request placeOrder mutation in conjunction with setPaymentMethodOnCart mutation
 * (for credit card payment method)
 */
export const createPlaceOrderTaskForCreditCard: (
  params: PlaceOrderWithPaymentMethodMutationVariables,
) => PlaceOrderTask = ramdaPipe(
  (params) => (cartToValidate: CartFragment) =>
    of(
      params.cartId === cartToValidate.id
        ? { _tag: 'Right' as const, data: params }
        : {
            _tag: 'Left' as const,
            error: new UnexpectedCartError(
              `Cart ID differs from state (expected: ${params.cartId}, actual: ${cartToValidate.id})`,
            ),
          },
    ),
  (validateParams) => (cart, dispensarySlug) =>
    validateParams(cart).pipe(
      switchMapRight(({ data: params }) =>
        combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
          take(1),
          switchMap(([client, token]) =>
            placeOrderWithPaymentMethodFactory(client, token, { ...params, dispensarySlug }).pipe(
              mapRight(({ data }) =>
                !isValidPlaceOrderWithPaymentMethodMutation(data)
                  ? {
                      _tag: 'Left' as const,
                      error: new InvalidResponseError(
                        'Incomplete data returned from placeOrderWithPaymentMethod mutation',
                      ),
                    }
                  : { _tag: 'Right' as const, data },
              ),
            ),
          ),
          // build CompletedCart from input cart and response data
          mapRight(({ data }) => ({
            _tag: 'Right' as const,
            data: {
              cart: {
                ...cart,
                ...data.setPaymentMethodOnCart.cart,
              },
              order: data.placeOrder.order,
              completedAt: new Date().toISOString(),
            },
          })),
        ),
      ),
    ),
  (effect: PlaceOrderTask['effect']) => ({
    effect,
    sensitive: true,
  }),
)
